import React, { useState, useMemo } from 'react';
import Sidebar from 'components/ui/Sidebar';
import Icon from 'components/AppIcon';

import ClientTable from './components/ClientTable';
import ClientFilters from './components/ClientFilters';
import AddClientModal from './components/AddClientModal';
import ClientDetailsModal from './components/ClientDetailsModal';
import BulkActionsModal from './components/BulkActionsModal';

const ClientManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilters, setSelectedFilters] = useState({
    clientType: 'all',
    county: 'all',
    caseCategory: 'all',
    relationshipStatus: 'all'
  });
  const [selectedClients, setSelectedClients] = useState([]);
  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
  const [isClientDetailsModalOpen, setIsClientDetailsModalOpen] = useState(false);
  const [isBulkActionsModalOpen, setIsBulkActionsModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  // Mock client data
  const mockClients = [
    {
      id: 1,
      name: "John Kamau Mwangi",
      email: "john.kamau@email.com",
      phone: "+254 712 345 678",
      clientType: "individual",
      county: "Nairobi",
      activeCases: 3,
      lastContact: new Date('2024-01-15'),
      totalBillings: 450000,
      relationshipStatus: "active",
      caseCategories: ["Criminal Law", "Family Law"],
      avatar: "https://randomuser.me/api/portraits/men/32.jpg",
      businessRegNumber: null,
      mpesaNumber: "+254 712 345 678",
      portalAccess: true,
      address: `P.O. Box 12345
Nairobi, Kenya`,
      notes: "Long-standing client with excellent payment history. Prefers communication via WhatsApp."
    },
    {
      id: 2,
      name: "Grace Wanjiku Enterprises Ltd",
      email: "info@gracewanjiku.co.ke",
      phone: "+254 720 987 654",
      clientType: "corporate",
      county: "Kiambu",
      activeCases: 2,
      lastContact: new Date('2024-01-12'),
      totalBillings: 1250000,
      relationshipStatus: "active",
      caseCategories: ["Corporate Law", "Contract Law"],
      avatar: "https://randomuser.me/api/portraits/women/45.jpg",
      businessRegNumber: "C.123456/2019",
      mpesaNumber: "+254 720 987 654",
      portalAccess: true,
      address: `Thika Road, Kiambu
P.O. Box 67890
Kiambu, Kenya`,
      notes: "Corporate client specializing in manufacturing. Regular legal advisory services required."
    },
    {
      id: 3,
      name: "Peter Ochieng Otieno",
      email: "peter.ochieng@gmail.com",
      phone: "+254 733 456 789",
      clientType: "individual",
      county: "Kisumu",
      activeCases: 1,
      lastContact: new Date('2024-01-10'),
      totalBillings: 180000,
      relationshipStatus: "active",
      caseCategories: ["Property Law"],
      avatar: "https://randomuser.me/api/portraits/men/28.jpg",
      businessRegNumber: null,
      mpesaNumber: "+254 733 456 789",
      portalAccess: false,
      address: `Milimani Estate
P.O. Box 54321
Kisumu, Kenya`,
      notes: "Property dispute case. Client prefers face-to-face meetings."
    },
    {
      id: 4,
      name: "Mary Njeri Consultancy",
      email: "mary@njericonsult.com",
      phone: "+254 745 123 456",
      clientType: "corporate",
      county: "Nakuru",
      activeCases: 4,
      lastContact: new Date('2024-01-08'),
      totalBillings: 890000,
      relationshipStatus: "active",
      caseCategories: ["Employment Law", "Corporate Law"],
      avatar: "https://randomuser.me/api/portraits/women/52.jpg",
      businessRegNumber: "C.789012/2020",
      mpesaNumber: "+254 745 123 456",
      portalAccess: true,
      address: `Nakuru Town
P.O. Box 98765
Nakuru, Kenya`,
      notes: "HR consultancy firm requiring regular employment law support."
    },
    {
      id: 5,
      name: "David Kiprop Ruto",
      email: "david.kiprop@yahoo.com",
      phone: "+254 756 789 012",
      clientType: "individual",
      county: "Uasin Gishu",
      activeCases: 0,
      lastContact: new Date('2023-12-20'),
      totalBillings: 75000,
      relationshipStatus: "inactive",
      caseCategories: ["Criminal Law"],
      avatar: "https://randomuser.me/api/portraits/men/41.jpg",
      businessRegNumber: null,
      mpesaNumber: "+254 756 789 012",
      portalAccess: false,
      address: `Eldoret Town
P.O. Box 11111
Eldoret, Kenya`,
      notes: "Case completed successfully. Potential for future referrals."
    },
    {
      id: 6,
      name: "Sarah Akinyi Holdings",
      email: "sarah@akinyiholdings.ke",
      phone: "+254 767 234 567",
      clientType: "corporate",
      county: "Mombasa",
      activeCases: 2,
      lastContact: new Date('2024-01-14'),
      totalBillings: 2100000,
      relationshipStatus: "active",
      caseCategories: ["Maritime Law", "Corporate Law"],
      avatar: "https://randomuser.me/api/portraits/women/38.jpg",
      businessRegNumber: "C.345678/2018",
      mpesaNumber: "+254 767 234 567",
      portalAccess: true,
      address: `Nyali, Mombasa
P.O. Box 22222
Mombasa, Kenya`,
      notes: "Major shipping company client. High-value cases requiring specialized maritime expertise."
    }
  ];

  // Filter and search clients
  const filteredClients = useMemo(() => {
    let filtered = mockClients.filter(client => {
      const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           client.phone.includes(searchTerm);
      
      const matchesType = selectedFilters.clientType === 'all' || client.clientType === selectedFilters.clientType;
      const matchesCounty = selectedFilters.county === 'all' || client.county === selectedFilters.county;
      const matchesCategory = selectedFilters.caseCategory === 'all' || 
                             client.caseCategories.includes(selectedFilters.caseCategory);
      const matchesStatus = selectedFilters.relationshipStatus === 'all' || 
                           client.relationshipStatus === selectedFilters.relationshipStatus;

      return matchesSearch && matchesType && matchesCounty && matchesCategory && matchesStatus;
    });

    // Sort clients
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'lastContact') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [mockClients, searchTerm, selectedFilters, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleClientSelect = (clientId) => {
    setSelectedClients(prev => 
      prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleSelectAll = () => {
    if (selectedClients.length === filteredClients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(filteredClients.map(client => client.id));
    }
  };

  const handleClientAction = (action, client) => {
    switch (action) {
      case 'view':
        setSelectedClient(client);
        setIsClientDetailsModalOpen(true);
        break;
      case 'call':
        window.open(`tel:${client.phone}`);
        break;
      case 'email':
        window.open(`mailto:${client.email}`);
        break;
      case 'sms':
        window.open(`sms:${client.phone}`);
        break;
      default:
        break;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(new Date(date));
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      
      <div className="md:ml-64 transition-layout">
        {/* Header */}
        <div className="bg-surface border-b border-border px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-heading font-bold text-text-primary">Client Management</h1>
              <p className="text-text-secondary mt-1">
                Manage client relationships and track case history
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              {selectedClients.length > 0 && (
                <button
                  onClick={() => setIsBulkActionsModalOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-secondary-100 text-text-primary rounded-lg hover:bg-secondary-200 transition-smooth focus:outline-none focus:ring-2 focus:ring-secondary-400"
                >
                  <Icon name="Settings" size={16} />
                  <span className="hidden sm:inline">Bulk Actions ({selectedClients.length})</span>
                  <span className="sm:hidden">Actions ({selectedClients.length})</span>
                </button>
              )}
              
              <button
                onClick={() => setIsAddClientModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition-smooth focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                <Icon name="Plus" size={16} />
                <span className="hidden sm:inline">Add Client</span>
                <span className="sm:hidden">Add</span>
              </button>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="p-6">
          <div className="bg-surface rounded-lg shadow-card p-6 mb-6">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Icon name="Search" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary" />
                  <input
                    type="text"
                    placeholder="Search clients by name, email, or phone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>

              {/* Filters */}
              <ClientFilters
                selectedFilters={selectedFilters}
                onFiltersChange={setSelectedFilters}
              />
            </div>
          </div>

          {/* Results Summary */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-text-secondary">
              Showing {filteredClients.length} of {mockClients.length} clients
            </p>
            
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 text-sm text-text-secondary">
                <input
                  type="checkbox"
                  checked={selectedClients.length === filteredClients.length && filteredClients.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-border focus:ring-primary"
                />
                Select All
              </label>
            </div>
          </div>

          {/* Client Table */}
          <ClientTable
            clients={filteredClients}
            selectedClients={selectedClients}
            sortConfig={sortConfig}
            onSort={handleSort}
            onClientSelect={handleClientSelect}
            onClientAction={handleClientAction}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
          />
        </div>
      </div>

      {/* Modals */}
      {isAddClientModalOpen && (
        <AddClientModal
          isOpen={isAddClientModalOpen}
          onClose={() => setIsAddClientModalOpen(false)}
        />
      )}

      {isClientDetailsModalOpen && selectedClient && (
        <ClientDetailsModal
          isOpen={isClientDetailsModalOpen}
          onClose={() => setIsClientDetailsModalOpen(false)}
          client={selectedClient}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
        />
      )}

      {isBulkActionsModalOpen && (
        <BulkActionsModal
          isOpen={isBulkActionsModalOpen}
          onClose={() => setIsBulkActionsModalOpen(false)}
          selectedClients={selectedClients}
          clients={mockClients}
          onClearSelection={() => setSelectedClients([])}
        />
      )}
    </div>
  );
};

export default ClientManagement;